/**
 * MFA Verification Component
 * 
 * Displayed during login when user has MFA enabled.
 * Handles both Email OTP and TOTP verification.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Shield, Mail, Smartphone, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { mfaAPI, authAPI } from '../../utils/api';

const MFAVerification = ({ 
  userId, 
  mfaMethod, 
  onSuccess, 
  onCancel,
  userEmail 
}) => {
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState(null);
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [countdown, setCountdown] = useState(0);
  
  const inputRef = useRef(null);
  
  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  
  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  // Auto-send challenge for email method on mount
  // Auto-send challenge for email method on mount
  useEffect(() => {
    if (mfaMethod === 'email') {
      sendChallenge();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaMethod]);
  
  const sendChallenge = async () => {
    setSendingCode(true);
    setError(null);
    
    try {
      const response = await mfaAPI.createChallenge(userId, mfaMethod);
      setChallengeId(response.challenge_id);
      setCountdown(60); // 60 second cooldown for resend
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };
  
  const handleVerify = async () => {
    if (!code || code.length < 6) {
      setError('Please enter a valid code');
      return;
    }
    
    // For TOTP, create challenge first if not exists
    let currentChallengeId = challengeId;
    if (!currentChallengeId && mfaMethod === 'totp') {
      try {
        const response = await mfaAPI.createChallenge(userId, mfaMethod);
        currentChallengeId = response.challenge_id;
        setChallengeId(currentChallengeId);
      } catch (err) {
        setError(err.message || 'Failed to create challenge');
        return;
      }
    }
    
    if (!currentChallengeId) {
      setError('No active challenge. Please request a new code.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Verify the MFA code
      await mfaAPI.verifyChallenge(currentChallengeId, code);
      
      // Complete login
      const loginResponse = await authAPI.completeMfaLogin(userId, currentChallengeId);
      
      // Success - pass the login response back
      onSuccess?.(loginResponse);
      
    } catch (err) {
      setError(err.message || 'Invalid verification code');
      // Clear input on error
      setCode('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && code.length >= 6) {
      handleVerify();
    }
  };
  
  const maskedEmail = userEmail ? 
    userEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3') : 
    'your email';
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex p-3 bg-blue-100 rounded-full mb-4">
              {mfaMethod === 'totp' ? (
                <Smartphone className="h-8 w-8 text-blue-600" />
              ) : (
                <Mail className="h-8 w-8 text-blue-600" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h1>
            <p className="text-gray-600 mt-2">
              {mfaMethod === 'totp' 
                ? 'Enter the code from your authenticator app'
                : `Enter the code sent to ${maskedEmail}`
              }
            </p>
          </div>
          
          {/* Code Input */}
          <div className="space-y-4">
            <div>
              <Input
                ref={inputRef}
                type="text"
                maxLength={10}
                placeholder="Enter code"
                value={code}
                onChange={(e) => {
                  // Allow alphanumeric for backup codes
                  setCode(e.target.value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase());
                  setError(null);
                }}
                onKeyDown={handleKeyDown}
                className="text-center text-2xl tracking-widest font-mono h-14"
                disabled={loading}
                data-testid="mfa-code-input"
              />
              {error && (
                <p className="text-sm text-red-600 mt-2 text-center">{error}</p>
              )}
            </div>
            
            <Button 
              onClick={handleVerify}
              disabled={loading || code.length < 6}
              className="w-full h-12"
              data-testid="mfa-verify-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>
          </div>
          
          {/* Resend for email method */}
          {mfaMethod === 'email' && (
            <div className="mt-4 text-center">
              {sendingCode ? (
                <span className="text-gray-500 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
                  Sending code...
                </span>
              ) : countdown > 0 ? (
                <span className="text-gray-500 text-sm">
                  Resend code in {countdown}s
                </span>
              ) : (
                <button
                  onClick={sendChallenge}
                  className="text-blue-600 text-sm hover:underline"
                  data-testid="mfa-resend-code"
                >
                  Didn&apos;t receive the code? Resend
                </button>
              )}
            </div>
          )}
          
          {/* Backup code hint */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Lost access to your authenticator? Use a{' '}
              <span className="font-medium">backup code</span> instead.
            </p>
          </div>
          
          {/* Back button */}
          <div className="mt-4">
            <button
              onClick={onCancel}
              className="flex items-center justify-center w-full text-gray-600 text-sm hover:text-gray-900"
              data-testid="mfa-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
